import type { Timestamp } from 'firebase/firestore'

/* ============================================================================
   Firestore document shapes.

   These mirror the Swift structs in `Encore/Encore/Encore/Models/` field for
   field. They are a CONTRACT, not a convenience: the Cloud Functions that send
   push and queue email are triggered by writes to these collections and read
   these exact field names, so a web write that drifts is a notification that
   silently never arrives.

   Swift `@DocumentID` becomes `id`, which Firestore does not store in the
   document — it is attached from the snapshot on read and must be stripped on
   write. Swift optionals become `?`; Swift `Timestamp` stays `Timestamp`.
   ============================================================================ */

/** Fields Firestore stores. `id` is attached on read, never written. */
export type WithId<T> = T & { id: string }

// ------------------------------------------------ orgs, artists, people

/**
 * The account holder.
 *
 * One per signup today. It exists from day one so the schema has a single
 * shape rather than two, and so the day someone adds a second artist is a
 * normal Tuesday rather than a migration.
 */
export interface Organisation {
  name: string
  ownerId: string
  /** uid → membership. Same shape as `Tour.members`, for the same reason:
   *  rules cannot run queries, so membership is denormalised here. */
  members?: Record<string, TourMembership>
  brandingLogoURL?: string | null
  createdAt?: Timestamp
}

/**
 * An artist on the roster.
 *
 * A tour is temporary; an artist is permanent. It holds what survives between
 * runs, and it is the permission boundary an agency needs — "Sam can see these
 * three artists and nothing else".
 */
/**
 * Solo act or band.
 *
 * The distinction is real rather than cosmetic: a band has members whose
 * passports, dietary requirements and visas all have to travel with it, and a
 * solo act does not. Everything else about an artist is identical.
 */
export type ArtistKind = 'solo' | 'band'

export interface Artist {
  orgId: string
  name: string
  /** Absent on documents written before bands existed — read as `solo`. */
  kind?: ArtistKind
  /** Who is in the band. Empty for a solo act. */
  members?: BandMember[]
  avatarURL?: string | null
  /** How the picture is framed in the hero, per platform: a desktop hero is a
   *  wide letterbox and a phone hero is nearly square, so a crop that centres
   *  an artist on one leaves them off the edge of the other. */
  avatarScale?: number | null
  avatarOffsetX?: number | null
  avatarOffsetY?: number | null
  avatarScaleMobile?: number | null
  avatarOffsetXMobile?: number | null
  avatarOffsetYMobile?: number | null
  /** Archived, never deleted: tours point at artists, and a dangling
   *  reference loses the tour's history rather than the artist's. */
  archived?: boolean
  /**
   * Created by `provisionAccount` rather than by a person.
   *
   * Every account gets one so a solo user's tours have something to hang off,
   * and a solo user never sees it. On an agency roster it is the account
   * holder's own name sitting among their clients, so it is hidden there until
   * it has actually been used.
   */
  autoCreated?: boolean
  /** Who a new tour seeds its crew list from. */
  defaultTouringParty?: PartyMember[]
  createdAt?: Timestamp
}

/**
 * Someone in the band.
 *
 * A `Person`, like everyone else who travels — the same record that holds
 * their passport and dietary requirements. `instrument` is what they play;
 * `PartyMember.role` is what they do on the road. A drummer who also drives
 * the van is both, and that is not a contradiction.
 */
export interface BandMember {
  personId: string
  instrument: string
}

export interface PartyMember {
  personId: string
  role: string
  defaultIncluded: boolean
}

/**
 * Someone who travels, at ORG level rather than artist level.
 *
 * The same FOH engineer works for three acts on the roster, and their passport
 * should be entered once. This is what replaces the per-tour duplication on
 * `Contact` and `tourCrew`.
 */
export interface Person {
  orgId: string
  name: string
  email?: string | null
  phone?: string | null
  passport?: PassportInfo | null
  nationality?: string | null
  visaNotes?: string | null
  loyaltyPrograms?: LoyaltyProgram[] | null
  seatPreference?: string | null
  dietaryRequirements?: string | null
  allergies?: string | null
  roomPreference?: string | null
  emergencyContact?: EmergencyContact | null
  createdAt?: Timestamp
}

// ---------------------------------------------------------------- tours

export type LandingPageTheme = 'Dark Mode' | 'Print'

export interface Tour {
  ownerId: string
  tourName: string
  artist: string
  startDate: Timestamp
  endDate: Timestamp
  posterURL?: string | null

  /** uid → role. Read by the security rules to answer "is this user on this
   *  tour?" in one get(). Rules cannot run queries and tourCrew uses
   *  auto-generated IDs, so there is no other path. `tourCrew` remains the
   *  source of truth for crew detail; this is an index written alongside it. */
  members?: Record<string, TourMembership>

  landingPageUrl?: string | null
  landingPageHeaderImageUrl?: string | null
  landingPageHeaderVideoUrl?: string | null
  landingPageScrapbookImageUrl2?: string | null
  landingPageHeaderFocusY?: number | null
  landingPageBio?: string | null
  landingPageTheme?: LandingPageTheme | null
  isLandingPagePublished?: boolean | null

  stagePlotImageURL?: string | null
  defaultEventDescription?: string | null
  defaultImportantInfo?: string | null

  /**
   * How the poster is framed in the hero.
   *
   * `posterOffsetY` predates the other two — the Mac has always let you drag
   * the artwork up and down. Scale and X came with pinch-to-zoom, and the
   * offsets are fractions of the frame so the crop holds at any width.
   */
  posterOffsetY?: number | null
  posterOffsetX?: number | null
  posterScale?: number | null

  /** The artist this tour belongs to. Optional only for documents written
   *  before artists existed — `provisionAccount` adopts those on next launch. */
  artistId?: string | null

  createdAt?: Timestamp
}

/**
 * Roles the `members` map may carry.
 *
 * `'crew'` is the legacy value: every existing client writes that literal
 * string for everyone regardless of access level, which is why the current
 * rules cannot tell an editor from a viewer. New writes use the real level so
 * the rules can eventually drop `'crew'` and make viewers read-only for real.
 */
export type TourMemberRole = 'owner' | 'admin' | 'editor' | 'viewer' | 'crew'

/**
 * One person's membership of a tour, as the security rules read it.
 *
 * The rules cannot reach `tourCrew` — auto-generated ids, and rules cannot
 * query — so everything they must enforce is denormalised here. That includes
 * the date window: a window that lives only on the crew document is one the UI
 * honours and the SDK ignores.
 *
 * `null` on either bound means unbounded, which is the common case.
 */
export interface TourMembership {
  level: TourMemberRole
  from?: Timestamp | null
  until?: Timestamp | null
}

// ---------------------------------------------------------------- shows

export interface Show {
  tourId: string
  date: Timestamp
  city: string
  country?: string | null
  venueName: string
  venueAddress: string
  timezone?: string | null
  scanCode?: string | null
  showSpecificPosterUrl?: string | null

  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null

  venueAccess?: Timestamp | null
  loadIn?: Timestamp | null
  soundCheck?: Timestamp | null
  doorsOpen?: Timestamp | null
  headlinerSetTime?: Timestamp | null
  headlinerSetDurationMinutes?: number | null
  packOut?: Timestamp | null
  packOutNextDay?: boolean | null
  supportActIds?: string[] | null

  /** Geocoded venue position, cached so the map is instant after the first
   *  view. Additive — the Swift decoder ignores keys it doesn't know. */
  venueLat?: number | null
  venueLon?: number | null

  createdAt?: Timestamp
}

// ------------------------------------------------------- itinerary items

export interface ItineraryItem {
  ownerId: string
  tourId: string
  showId?: string | null
  title: string
  /** Matches `ItineraryItemType.rawValue` in the Swift app. */
  type: string
  timeUTC: Timestamp

  subtitle?: string | null
  notes?: string | null
  timezone?: string | null
  isShowTiming?: boolean | null

  /**
   * The item exists but nobody has said when it happens yet.
   *
   * Show presets are seeded the moment a show is saved, before times are
   * known. `timeUTC` stays required — every sort, day grouping, export and
   * notification path depends on a concrete instant — so unset items park at
   * the start of the show day and the row offers "Set time" until this clears.
   */
  needsTime?: boolean | null

  visibility?: string | null
  visibleTo?: string[] | null

  /** Carrier code, when the item was generated from a flight. */
  airlineCode?: string | null
}

// -------------------------------------------------------------- travel

export interface Passenger {
  crewId: string
  baggage?: string | null
  /** Seat, e.g. "14A". Additive — the Swift `Passenger` decoder ignores keys
   *  it doesn't know, so writing this is safe for the native apps. */
  seat?: string | null
}

export interface Flight {
  tourId: string
  ownerId: string
  airline?: string | null
  flightNumber?: string | null
  departureTimeUTC: Timestamp
  arrivalTimeUTC: Timestamp
  origin: string
  destination: string
  notes?: string | null
  passengers: Passenger[]
  /** Airline booking reference / PNR. Additive, same as `Passenger.seat`. */
  bookingReference?: string | null
}

export interface HotelGuest {
  crewId: string
  name: string
}

export interface HotelRoom {
  id: string
  roomNumber?: string | null
  guests: HotelGuest[]
}

export interface Hotel {
  tourId: string
  ownerId: string
  name: string
  address: string
  city: string
  country: string
  /** Non-optional in the Swift model. Don't write a hotel without it. */
  timezone: string
  checkInDate: Timestamp
  checkOutDate: Timestamp
  bookingReference?: string | null
  rooms: HotelRoom[]
  createdAt?: Timestamp
}

// --------------------------------------------------------- guest list

/**
 * A guest on a show's list. Lives at `shows/{showId}/guestlist/{id}`.
 *
 * The Swift model writes `note` and `additionalGuests` as empty strings rather
 * than omitting them, and reads `additionalGuests` as a *string* — it is a
 * count typed into a text field, not a number. Keep both quirks.
 */
export interface GuestListItem {
  name: string
  note?: string
  additionalGuests?: string
}

// ------------------------------------------------------- support acts

export interface SupportAct {
  tourId: string
  name: string
  /** "Touring" travels with the tour; "Local" is booked per city. */
  type: 'Touring' | 'Local'
  contactEmail?: string | null
  createdAt?: Timestamp
}

// ---------------------------------------------------------------- crew

export type CrewAccessLevel = 'editor' | 'viewer'
/** Legacy three-tier model. Read to migrate old documents; never written. */
export type CrewVisibility = 'full' | 'limited' | 'temporary'
export type InviteStatus = 'pending' | 'invited' | 'accepted'

export interface TourCrew {
  tourId: string
  userId?: string | null
  contactId?: string | null
  /** The org-level `Person` this crew record is. Written when a tour seeds
   *  its crew from an artist's touring party, and the only thing that lets
   *  the roster spot the same engineer double-booked across two artists —
   *  `contactId` is per-owner and `name` is not an identity. */
  personId?: string | null
  name: string
  email?: string | null
  phone?: string | null
  roles: string[]
  visibility?: CrewVisibility | null
  accessLevel?: CrewAccessLevel | null
  status: InviteStatus
  invitationCode?: string | null
  startDate?: Timestamp | null
  endDate?: Timestamp | null
  invitedBy: string
  createdAt?: Timestamp
}

/** Editor/Viewer, falling back to the pre-simplification field. */
export function resolvedAccessLevel(crew: Pick<TourCrew, 'accessLevel' | 'visibility'>): CrewAccessLevel {
  if (crew.accessLevel) return crew.accessLevel
  return crew.visibility === 'full' ? 'editor' : 'viewer'
}

// ------------------------------------------------------- address book

export interface Venue {
  ownerId: string
  name: string
  address?: string | null
  city: string
  country?: string | null
  capacity?: number | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  /** Link to the `contacts` document; the fields above are a denormalised
   *  copy so a venue row renders without a second read. */
  venueContactId?: string | null
  createdAt?: Timestamp
}

export interface LoyaltyProgram {
  id: string
  airline: string
  accountNumber: string
}

export interface PassportInfo {
  passportNumber: string
  issuedDate: Timestamp
  expiryDate: Timestamp
  issuingCountry: string
}

export interface DocumentInfo {
  id?: string
  name: string
  url: string
}

export interface EmergencyContact {
  name: string
  phone: string
}

export interface Contact {
  ownerId: string
  name: string
  roles: string[]
  email?: string | null
  phone?: string | null
  notes?: string | null
  location?: string | null
  profileImageURL?: string | null
  dateOfBirth?: Timestamp | null
  countryOfBirth?: string | null
  passport?: PassportInfo | null
  documents?: DocumentInfo[] | null
  emergencyContact?: EmergencyContact | null
  allergies?: string | null
  medications?: string | null
  loyaltyPrograms?: LoyaltyProgram[] | null
  linkedUserId?: string | null
  createdAt?: Timestamp
}

// ------------------------------------------------------------- account

export interface EncoreUser {
  /** Readable by any signed-in user — crew lists render names and avatars, and
   *  invites resolve an email to an account before sending. Nothing private
   *  belongs in this document. */
  email?: string | null
  displayName?: string | null
  photoURL?: string | null
  role?: string | null
  hasCompletedFirstRunTutorial?: boolean
  hasCompletedTourCreationTutorial?: boolean
  fcmTokens?: string[]
  timezone?: string | null
}

// ------------------------------------------------------- notifications

export interface AppNotification {
  userId: string
  recipientId?: string | null
  kind?: string | null
  title?: string | null
  body?: string | null
  tourId?: string | null
  hasBeenRead?: boolean
  createdAt?: Timestamp

  // Invitation payload
  inviterId?: string | null
  inviterName?: string | null
  tourName?: string | null
  artistName?: string | null
  crewDocId?: string | null
  roles?: string[] | null
}

// -------------------------------------------------------------- invites

export interface Invitation {
  tourId: string
  crewDocId: string
  inviterId: string
  createdAt: Timestamp
  expiresAt: Timestamp
}

// ----------------------------------------------------------------- mail

/**
 * A queued message. `processMailQueue` in encore-functions picks these up and
 * delivers through Resend; Resend's own templates are bypassed, the HTML here
 * is what gets sent. A successful write means queued, not delivered.
 */
export interface MailDocument {
  to: string[]
  message: {
    subject: string
    html: string
    text: string
  }
  delivery?: unknown
}
